import { useState } from "react";

import {
  Box,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Tooltip,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  MenuList,
  ListItemText,
  ListItemIcon,
  Avatar,
  useTheme
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faChevronCircleDown,
  faDatabase,
  faReceipt,
  faQuestionCircle,
  faTag,
  faCalendar,
  faShirt
} from "@fortawesome/pro-solid-svg-icons";

import { editObjectData } from "@aclymatepackages/array-immutability-helpers";
import { nameToInitials } from "@aclymatepackages/converters";
import { emissionsCategoriesTags } from "@aclymatepackages/subcategories";
import { AnimatedLogo, Checkbox, TextField } from "@aclymatepackages/atoms";
import { formatDate, ucFirstLetters } from "@aclymatepackages/formatters";
import { truncateText } from "@aclymatepackages/other-helpers";
import mainTheme from "@aclymatepackages/themes";

import EditableNameInput from "../../atoms/inputs/EditableNameInput";
import Card from "../../atoms/mui/Card";
import DocumentationDownloadButton from "../../atoms/buttons/DocumentationDownloadButton";
import SortableTable, {
  makeColumnObj
} from "../../modules/tables/SortableTable";
import DocumentationUploader from "../../modules/file-uploaders/DocumentationUploader";
import SlideLayout from "../../layouts/SlideLayout";
import DocumentationYear from "../../inputs/tags/DocumentationYear";
import DocumentationCustomTags from "../../inputs/tags/DocumentationCustomTags";
import SmartUploadLinkedActivity from "../../modules/smart-upload/SmartUploadLinkedActivity";

import {
  useCachedDisplayData,
  useCachedFirebaseCrud,
  deleteFileFromStorage
} from "../../../helpers/firebase";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";
import usePermissions from "../../../helpers/hooks/usePermissions";
import {
  isSmartUploadDocument,
  isVisibleDocumentationFile
} from "../../../helpers/smartUpload";

//TODO: Need to add in products as a tag option once that's been merged in.
const tagsWithTransactions = [
  ...emissionsCategoriesTags,
  {
    name: "transactions",
    icon: <FontAwesomeIcon icon={faReceipt} style={{ color: "white" }} />,
    color: mainTheme.palette.secondary.main
  },
  {
    name: "products",
    color: mainTheme.palette["purchased-goods-services"].main,
    icon: <FontAwesomeIcon icon={faShirt} style={{ color: "white" }} />
  }
];

const DocumentationAvatar = () => {
  const { palette } = useTheme();
  return (
    <Avatar style={{ backgroundColor: palette.secondary.main }}>
      <InsertDriveFileIcon />
    </Avatar>
  );
};

const TagsFilterChipMenu = ({
  tags,
  selectedTags,
  onSelectTag,
  backgroundColor,
  label,
  icon
}) => {
  const { palette } = useTheme();
  const color = palette.text.primary;

  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <>
      <Chip
        icon={<FontAwesomeIcon icon={icon} style={{ color }} />}
        label={label}
        style={{ backgroundColor, color }}
        clickable
        onClick={(e) => setAnchorEl(e.currentTarget)}
        variant={selectedTags.length ? "filled" : "outlined"}
        deleteIcon={
          <FontAwesomeIcon
            icon={faChevronCircleDown}
            style={{
              color,
              fontSize: "1rem"
            }}
          />
        }
        onDelete={(e) => setAnchorEl(e.currentTarget)}
      />
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <MenuList>
          {tags.map(({ name, value }) => (
            <MenuItem key={name} onClick={() => onSelectTag(value)}>
              <ListItemIcon>
                <Checkbox
                  value={selectedTags.includes(value)}
                  editValue={() => onSelectTag(value)}
                />
              </ListItemIcon>
              <ListItemText>{name}</ListItemText>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </>
  );
};

const DocumentationTableRow = ({
  onClick,
  description,
  dateCreated,
  yearTag,
  name,
  type,
  uploadUser,
  linkedDoc,
  customTags = [],
  url
}) => {
  const { name: linkedDocName } = linkedDoc || {};
  const { name: userName } = uploadUser || {};

  const { palette } = useTheme();

  const findTypeProps = () => {
    if (!type) {
      return {};
    }

    return tagsWithTransactions.find(({ name }) => name === type) || {};
  };

  const { name: tagName, icon, color } = findTypeProps();

  const columns = [
    <Grid key="name" container spacing={1}>
      <Grid item>{name}</Grid>
      {description && (
        <Grid item>
          <Tooltip title={description}>
            <FontAwesomeIcon icon={faQuestionCircle} />
          </Tooltip>
        </Grid>
      )}
    </Grid>,
    formatDate(dateCreated),
    <Tooltip key="uploader" title={userName}>
      <Avatar style={{ backgroundColor: palette.flights.main }}>
        {nameToInitials(userName)}
      </Avatar>
    </Tooltip>,
    type ? (
      <Tooltip title={tagName}>
        <Avatar style={{ backgroundColor: color }}>{icon}</Avatar>
      </Tooltip>
    ) : null,
    linkedDocName ? (
      <Chip
        label={truncateText(linkedDocName)}
        style={{ backgroundColor: color, color: "white" }}
      />
    ) : null,
    yearTag ? <Chip label={yearTag} size="small" /> : null,
    customTags.length ? (
      <Grid container spacing={1}>
        {customTags.map((tag) => (
          <Grid item key={tag}>
            <Chip label={tag} size="small" />
          </Grid>
        ))}
      </Grid>
    ) : null,
    <DocumentationDownloadButton key="download" name={name} url={url} />
  ];

  return (
    <TableRow hover style={{ cursor: "pointer" }} onClick={onClick}>
      {columns.map((column, index) => (
        <TableCell key={index}>{column}</TableCell>
      ))}
    </TableRow>
  );
};

const DocumentationDetailsSlider = ({
  documentation,
  setSelectedDocumentation
}) => {
  const { updateCollectionDoc, deleteCachedCollectionDoc } =
    useCachedFirebaseCrud();

  const [newTag, setNewTag] = useState("");
  const [editableDocumentation, setEditableDocumentation] =
    useState(documentation);

  const {
    name,
    path,
    id,
    description,
    yearTag,
    customTags = [],
    linkedTransactionIds
  } = editableDocumentation;
  const [originalName] = path.split("/").reverse();
  const isSmartUpload = isSmartUploadDocument(documentation);

  const editDocumentation = (field) => (value) =>
    editObjectData(setEditableDocumentation, field, value);

  const onDeleteDocumentationFile = () => {
    deleteCachedCollectionDoc("documentation-files", id);
    return deleteFileFromStorage(path);
  };

  const onSaveChanges = async () => {
    await updateCollectionDoc("documentation-files", id, {
      ...editableDocumentation,
      customTags: [...customTags, ...(newTag ? [newTag] : [])]
    });
    return setSelectedDocumentation(null);
  };

  return (
    <SlideLayout
      isSlideOpen
      setIsSlideOpen={setSelectedDocumentation}
      header={
        <Grid container spacing={2}>
          <Grid item>
            <DocumentationAvatar />
          </Grid>
          <Grid item>
            {isSmartUpload ? (
              <>
                <Typography variant="h5">{name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {truncateText(originalName, 40)}
                </Typography>
              </>
            ) : (
              <EditableNameInput
                name={name}
                id={id}
                collection="documentation-files"
                helperText={truncateText(originalName, 40)}
              />
            )}
          </Grid>
        </Grid>
      }
      content={
        isSmartUpload ? (
          <Grid container direction="column" spacing={2}>
            {!!description && (
              <Grid item>
                <Typography variant="body2">{description}</Typography>
              </Grid>
            )}
            <Grid item>
              <SmartUploadLinkedActivity
                linkedTransactionIds={linkedTransactionIds}
              />
            </Grid>
            <Grid item>
              <Typography variant="caption" color="textSecondary">
                This document backs recorded activity, so it can&apos;t be
                edited or deleted here.
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                value={description}
                setValue={editDocumentation("description")}
                label="File Description (optional)"
                multiline
                fullWidth
                minRows={3}
              />
            </Grid>
            <Grid item container spacing={2} alignItems="flex-start">
              <Grid item xs={6}>
                <DocumentationYear
                  yearTag={yearTag}
                  editYearTag={editDocumentation("yearTag")}
                />
              </Grid>
              <Grid item xs={6}>
                <DocumentationCustomTags
                  newTag={newTag}
                  setNewTag={setNewTag}
                  customTags={customTags}
                  editCustomTags={editDocumentation("customTags")}
                />
              </Grid>
            </Grid>
          </Grid>
        )
      }
      footer={
        !isSmartUpload && (
          <Grid container justifyContent="space-between">
            <Grid item>
              <Tooltip title="Delete this document">
                <IconButton onClick={onDeleteDocumentationFile}>
                  <DeleteIcon color="error" />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={onSaveChanges}
              >
                Save Changes
              </Button>
            </Grid>
          </Grid>
        )
      }
    />
  );
};

const DocumentationInterface = ({ documentation }) => {
  const availableTypeTags = [
    ...new Set(documentation.map(({ type }) => type).filter(Boolean))
  ].map((type) => ({
    name: ucFirstLetters(type),
    value: type
  }));

  const availableYearTags = [
    ...new Set(documentation.map(({ yearTag }) => yearTag).filter(Boolean))
  ].map((yearTag) => ({ name: yearTag, value: yearTag }));

  const availableCustomTags = [
    ...new Set(
      documentation
        .filter(({ customTags }) => !!customTags)
        .flatMap(({ customTags }) => customTags)
    )
  ].map((tag) => ({ name: tag, value: tag }));

  const initialSelectedTags = {
    yearTags: [],
    typeTags: [],
    customTags: []
  };
  const [selectedTags, setSelectedTags] = useState(initialSelectedTags);
  const [showAllDocumentation, setShowAllDocumentation] = useState(true);
  const [selectedDocumentation, setSelectedDocumentation] = useState(null);

  const columns = [
    makeColumnObj("NAME", "name", true),
    makeColumnObj("DATE UPLOADED", "dateCreated", true),
    makeColumnObj("USER", "uploadUser.name", true),
    makeColumnObj("DOCUMENT TYPE", "type", true),
    makeColumnObj("TAGGED ENTITY"),
    makeColumnObj("TAGGED YEAR", "yearTag", true),
    makeColumnObj("CUSTOM TAGS"),
    makeColumnObj("DOWNLOAD")
  ];

  const editSelectedTags = (tagType) => (value) => {
    const typeTags = selectedTags[tagType];
    if (!typeTags) {
      return;
    }

    if (typeTags.includes(value)) {
      const newTypeTags = typeTags.filter((tag) => tag !== value);
      return setSelectedTags({
        ...selectedTags,
        [tagType]: newTypeTags
      });
    }

    setShowAllDocumentation(false);
    return setSelectedTags({
      ...selectedTags,
      [tagType]: [...typeTags, value]
    });
  };

  const filteredDocumentation = () => {
    if (showAllDocumentation) {
      return documentation;
    }

    const { yearTags, typeTags, customTags } = selectedTags;

    const filterByType = (type) => {
      if (!typeTags.length) {
        return true;
      }
      return (
        (typeTags.includes("untagged") && !type) || typeTags.includes(type)
      );
    };

    return documentation.filter(
      ({ type, yearTag, customTags: documentationCustomTags = [] }) => {
        const filteredByType = filterByType(type);
        const filterByYear = yearTags.length
          ? yearTags.includes(yearTag)
          : true;
        const filterByCustomTags = customTags.length
          ? customTags.some((tag) => documentationCustomTags.includes(tag))
          : true;

        return filteredByType && filterByYear && filterByCustomTags;
      }
    );
  };

  const buildTagMenus = () => {
    const typeTagsArray = availableTypeTags.length
      ? [
          {
            tagsOptions: [
              { name: "Untagged", value: "untagged" },
              ...availableTypeTags
            ],
            tagType: "typeTags",
            label: "Tagged Entities",
            icon: faDatabase
          }
        ]
      : [];

    const yearTagsArray = availableYearTags.length
      ? [
          {
            tagsOptions: availableYearTags,
            tagType: "yearTags",
            label: "Tagged Years",
            icon: faCalendar
          }
        ]
      : [];

    const customTagsArray = availableCustomTags.length
      ? [
          {
            tagsOptions: availableCustomTags,
            tagType: "customTags",
            label: "Custom Tags",
            icon: faTag
          }
        ]
      : [];

    return [...typeTagsArray, ...yearTagsArray, ...customTagsArray];
  };

  const tagMenus = buildTagMenus();
  const areAnyTagsSelected = Object.values(selectedTags).some(
    (tagsArray) => !!tagsArray.length
  );

  return (
    <>
      {selectedDocumentation && (
        <DocumentationDetailsSlider
          documentation={selectedDocumentation}
          setSelectedDocumentation={setSelectedDocumentation}
        />
      )}
      <Grid container direction="column" spacing={2}>
        {!!tagMenus.length && (
          <Grid item container spacing={1}>
            <Grid item>
              <Chip
                label="All Documents"
                onClick={() => {
                  setSelectedTags(initialSelectedTags);
                  return setShowAllDocumentation(true);
                }}
                variant={
                  showAllDocumentation || !areAnyTagsSelected
                    ? "filled"
                    : "outlined"
                }
                icon={
                  showAllDocumentation || !areAnyTagsSelected ? (
                    <CheckCircleIcon />
                  ) : null
                }
              />
            </Grid>
            {tagMenus.map(({ tagsOptions, tagType, ...chipMenuProps }) => (
              <Grid item key={tagType}>
                <TagsFilterChipMenu
                  tags={tagsOptions}
                  selectedTags={selectedTags[tagType] || []}
                  onSelectTag={editSelectedTags(tagType)}
                  {...chipMenuProps}
                />
              </Grid>
            ))}
          </Grid>
        )}
        <Grid item>
          {documentation.length ? (
            <SortableTable
              rows={filteredDocumentation()}
              rowComponent={(documentation) => (
                <DocumentationTableRow
                  onClick={() => setSelectedDocumentation(documentation)}
                  {...documentation}
                />
              )}
              columns={columns}
            />
          ) : (
            <Typography variant="h6" align="center">
              You haven&apos;t uploaded any documentation files yet. You can use this
              documentation to keep track of important files related to your
              carbon accounting or reporting.
            </Typography>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const Documentation = () => {
  const { isSimulation } = usePlatformLayoutContext();
  const { canEdit } = usePermissions();

  const [documentation, documentationLoading] = useCachedDisplayData(
    "documentation-files"
  );

  const [addDocumentationOpen, setAddDocumentationOpen] = useState(false);

  return (
    <>
      {addDocumentationOpen && (
        <SlideLayout
          isSlideOpen
          setIsSlideOpen={setAddDocumentationOpen}
          header={
            <Typography variant="h3">Upload Untagged Document</Typography>
          }
          content={
            <DocumentationUploader
              showDocumentationFiles={false}
              onUpload={() => setAddDocumentationOpen(false)}
            />
          }
        />
      )}
      <Card
        title="Documentation"
        cardType="layout"
        icon={<DocumentationAvatar />}
        content={
          documentationLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
            >
              <AnimatedLogo />
            </Box>
          ) : (
            <DocumentationInterface
              documentation={documentation.filter(isVisibleDocumentationFile)}
            />
          )
        }
        action={
          <Tooltip
            title="This feature is disabled in simulation mode"
            disableHoverListener={!isSimulation}
          >
            <span>
              <Button
                onClick={() => setAddDocumentationOpen(true)}
                variant="contained"
                color="secondary"
                size="small"
                disabled={isSimulation || !canEdit}
              >
                Upload New Document
              </Button>
            </span>
          </Tooltip>
        }
      />
    </>
  );
};
export default Documentation;
